// DispatchDeck — Personal-vehicle mileage reimbursement (TASK-1483)
//
// PURE math module: no Firebase, no DOM, no AppState. Loaded in the browser via
// ONE side-effect import from clock.js (`import '../vehicleReimb.js?v=…'`) which
// sets window.VehicleReimb — every other module (jobboard.js, payroll.js, hr.js)
// reads window.VehicleReimb at call time (Pitfall #1: never a second `?v=`
// import). Node unit tests require() it directly (UMD):
// `node tests/vehicle-reimb.unit.cjs`.
//
// WHY THIS EXISTS. `hrPerDiem` was doing two jobs: a living allowance for some
// people and "truck pay" for others, both paid on a days-≥8h rule that has
// nothing to do with whether a personal vehicle was used. This is the separate
// concept: the driver reports that he used his own vehicle and how many miles,
// and DD computes the reimbursement from a TENANT rate — never from a dollar
// figure the driver typed.
//
// 🔴 BINDING (COD Adversary): the ONLY claim fields a driver writes are
// `personalVehicleUsed` (bool) and `personalVehicleMiles` (number). Every dollar
// figure is COMPUTED HERE at read/export time from the tenant policy in effect
// on the work date. Nothing in this module reads a stored amount/rate off a
// clock entry — a raw-SDK `personalVehicleAmount` is ignored even if the rules
// fence were somehow bypassed.
//
// DATA SHAPES
//   tenants/{tid}.settings.personalVehicle (written ONLY by the
//   setPersonalVehicleSettings callable — rules lock it on the client arms):
//     { enabled: bool, requireApproval: bool,
//       history: [ { effectiveFrom: 'YYYY-MM-DD', basis: 'per_mile'|'flat_per_day',
//                    ratePerMile: number ($/mile), flatPerDay: number|null ($/day),
//                    excessTaxable: bool (flat_per_day only; absent = false),
//                    qualify: { mode: 'miles'|'hours'|'both', minHours: number }
//                      (flat_per_day only; absent = { mode:'miles', minHours:8 }),
//                    setBy: email, setAt: ms } ] }
//     History is append-only; the policy in effect on a work date is the entry
//     with the latest effectiveFrom <= that date (ties → later setAt). This is
//     the COD "rate snapshot": a January IRS rate change is a NEW entry dated
//     Jan 1, so a pay period that straddles Jan 1 prices each day at its own
//     rate and an older period never recomputes.
//   people/{pid}.hrVehicleMileageRate — absent/0 = NOT eligible (mirrors the
//     hrPerDiem > 0 gate). A positive value marks the person eligible; it holds
//     the company rate at the moment they were enrolled, for display. PAYOUT
//     ALWAYS USES THE TENANT POLICY IN EFFECT ON THE WORK DATE — a per-person
//     number would go stale every January and could not be effective-dated.
//   clockEntries/{id}:
//     personalVehicleUsed: bool, personalVehicleMiles: number (0 < m <= 1000)
//     personalVehicleEnteredBy: { source:'admin', by, at } — admin-entered
//       (Add Hours / Edit entry), never set by a driver (rules).
//     personalVehicleReview: { status:'approved'|'denied', by, at, miles,
//       amount?, kind? } — `miles` = the miles reviewed and `amount` (TASK-9439)
//       = the amount it would pay when reviewed; a later change to EITHER voids
//       the decision (reviewBound). A review with no `amount` (1483-era) binds
//       to miles only. kind:'hoursDay' = written on every entry of a day that
//       earned a flat on hours alone (see APPROVAL BEFORE PAYROLL below).
//       workforce/admin only (rules).
//
// APPROVAL BEFORE PAYROLL (TASK-9439) — generalises 1483's requireApproval.
//   tenants/{tid}.settings.payrollApproval (written ONLY by the
//   setPayrollApprovalSettings callable; rules lock the key):
//     { blockExport: bool,
//       history: [ { effectiveFrom:'YYYY-MM-DD', types:['vehicle'], setBy, setAt } ] }
//   `types` = which driver-reported amounts need approval before they pay.
//   Effective-dated like the rate: a work date uses the row in effect on it,
//   so ticking/unticking a type never re-cuts a closed period. Before the
//   first row, 1483's un-dated personalVehicle.requireApproval is the
//   baseline (unchanged for tenants that never touch the new card).
//   `blockExport` is NOT dated: it only decides whether Payroll Prep's export
//   is disabled (ON) or warns (OFF) while a required item is pending; it never
//   changes an amount.
//
// TWO PAYMENT BASES (Chris 2026-08-11)
//   per_mile     → paid = miles × rate, all of it as a reimbursement; the
//                  taxable split is always 0.
//   flat_per_day → paid = flatPerDay on any day the vehicle was used (miles
//                  summed across that person's entries that day). The part
//                  the miles don't cover is `uncovered` = max(0, flat − miles ×
//                  rate) ($35 at 72.5¢ breaks even at 48.3 mi). HOW it is paid
//                  is the TENANT'S call, never DD's (Chris 2026-09-28), via the
//                  effective-dated `excessTaxable` switch:
//                    OFF (default) → the whole flat pays as a reimbursement;
//                                    `uncovered` is shown as information only.
//                    ON            → the uncovered part moves to taxableExcess
//                                    (exported in Gusto's Bonus column).
//   Field names below: `nonTaxable` = the amount paid AS A REIMBURSEMENT,
//   `taxableExcess` = the amount paid AS WAGES. Names kept for the payroll
//   plumbing; they describe routing, not a tax ruling.
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.VehicleReimb = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  // Keep in lockstep with firestore.rules (clockEntries self arm,
  // t1483VehicleClaimSelfOk) and functions setPersonalVehicleSettings.
  const MAX_MILES = 1000;          // per clock entry; 14h at 70 mph ≈ 980
  const MAX_RATE_PER_MILE = 2;     // $/mile sanity ceiling (IRS 2026 = 0.725)
  const MAX_FLAT_PER_DAY = 500;    // $/day sanity ceiling
  const BASES = ['per_mile', 'flat_per_day'];
  // Which days earn the flat (flat_per_day only; per_mile always pays miles).
  //   miles → any day with a miles claim   hours → any day with ≥ minHours
  //   worked (the TASK-939 per-diem day rule)   both → both on the same day.
  const QUALIFY_MODES = ['miles', 'hours', 'both'];
  const DEFAULT_MIN_HOURS = 8;     // TASK-939 per-diem rule; TITS truck pay today
  // The tenant's own name for this pay type (Chris: "Toothpaste pay" is fine).
  // A LABEL only — it never changes the math or the tax routing. Keep in
  // lockstep with setPersonalVehicleSettings (same clean + cap).
  const DEFAULT_PAY_TYPE_NAME = 'Vehicle Reimbursement';
  const MAX_PAY_TYPE_NAME = 40;
  // TASK-9439 — driver-reported amounts that can be made to need approval
  // before payroll. Only types that are self-reported AND reach the payroll
  // file are listed (verified 2026-09-28): standby/downtime/piece rate are
  // ticket lines billed to the CUSTOMER, never pay; per diem is computed from
  // hours × the HR rate (or office-entered payAdjustments), not claimed;
  // out-of-pocket expenses are approved and paid in AP, not in this file.
  const APPROVAL_TYPES = ['vehicle'];
  // Client-forbidden claim keys (rules deny a self write that touches any).
  const SERVER_ONLY_KEYS = [
    'personalVehicleAmount', 'personalVehicleRate', 'personalVehicleReimbursement',
    'personalVehicleExcess', 'personalVehicleReview', 'personalVehicleEnteredBy',
    'personalVehicleBasis', 'personalVehicleFlat',
  ];

  function round2(n) {
    const v = Number(n) || 0;
    return Math.round((v + (v >= 0 ? 1e-9 : -1e-9)) * 100) / 100;
  }

  function num(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }

  // Local calendar date (YYYY-MM-DD) of a Timestamp / {seconds} / ms / Date /
  // ISO. Local, not UTC — a CT evening shift must not land on tomorrow.
  function ymdOf(v) {
    let d = null;
    if (v === null || v === undefined || v === '') return null;
    if (v instanceof Date) d = v;
    else if (typeof v === 'number') d = new Date(v);
    else if (typeof v.toDate === 'function') d = v.toDate();
    else if (typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
    else if (typeof v === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
      d = new Date(v);
    }
    if (!d || isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  // ── eligibility ───────────────────────────────────────────────────────────
  function isEligible(person) {
    return !!person && (num(person.hrVehicleMileageRate) || 0) > 0;
  }

  // Cleaned display name, never empty: control characters stripped, runs of
  // whitespace collapsed, trimmed, capped at 40. Callers still ESCAPE it for
  // their context (HTML text/attribute, CSV) — this is not an escaper.
  function cleanPayTypeName(raw) {
    if (typeof raw !== 'string') return '';
    return raw.replace(/[\u0000-\u001F\u007F-\u009F\u2028\u2029]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, MAX_PAY_TYPE_NAME).trim();
  }
  function payTypeNameOf(settingsOrPolicy) {
    const s = settingsOrPolicy || {};
    const pv = s.personalVehicle && typeof s.personalVehicle === 'object' ? s.personalVehicle : s;
    return cleanPayTypeName(pv.payTypeName) || DEFAULT_PAY_TYPE_NAME;
  }

  // ── policy ────────────────────────────────────────────────────────────────
  function _validEntry(e) {
    if (!e || typeof e !== 'object') return false;
    if (typeof e.effectiveFrom !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(e.effectiveFrom)) return false;
    if (BASES.indexOf(e.basis) === -1) return false;
    const r = num(e.ratePerMile);
    if (r === null || r <= 0 || r > MAX_RATE_PER_MILE) return false;
    if (e.basis === 'flat_per_day') {
      const f = num(e.flatPerDay);
      if (f === null || f <= 0 || f > MAX_FLAT_PER_DAY) return false;
      // A present-but-malformed trigger drops the row (never guessed).
      if (e.qualify !== undefined && e.qualify !== null && _qualifyOf(e.qualify) === null) return false;
    }
    return true;
  }

  // { mode, minHours } or null when malformed. Absent → the default.
  function _qualifyOf(q) {
    if (q === undefined || q === null) return { mode: 'miles', minHours: DEFAULT_MIN_HOURS };
    if (typeof q !== 'object' || QUALIFY_MODES.indexOf(q.mode) === -1) return null;
    const h = q.minHours === undefined || q.minHours === null ? DEFAULT_MIN_HOURS : num(q.minHours);
    if (h === null || h <= 0 || h > 24) return null;
    return { mode: q.mode, minHours: h };
  }

  // settings → { enabled, requireApproval, history[] sorted asc }. Malformed
  // history rows are DROPPED (never guessed); a policy with no valid row is
  // treated as "no rate" by resolvePolicy, which callers surface as a flag.
  function policyOf(settingsOrPolicy) {
    const s = settingsOrPolicy || {};
    const pv = s.personalVehicle && typeof s.personalVehicle === 'object' ? s.personalVehicle : s;
    const hist = (Array.isArray(pv.history) ? pv.history : [])
      .filter(_validEntry)
      .map(e => ({
        effectiveFrom: e.effectiveFrom,
        basis: e.basis,
        ratePerMile: num(e.ratePerMile),
        flatPerDay: e.basis === 'flat_per_day' ? num(e.flatPerDay) : null,
        excessTaxable: e.basis === 'flat_per_day' && e.excessTaxable === true,
        qualify: e.basis === 'flat_per_day' ? _qualifyOf(e.qualify) : null,
        setBy: e.setBy || null,
        setAt: num(e.setAt) || 0,
      }))
      .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : a.effectiveFrom > b.effectiveFrom ? 1 : a.setAt - b.setAt));
    return {
      _normalized: true,
      enabled: pv.enabled === true,
      requireApproval: pv.requireApproval === true,
      approval: approvalPolicyOf(s),
      payTypeName: cleanPayTypeName(pv.payTypeName) || DEFAULT_PAY_TYPE_NAME,
      history: hist,
    };
  }

  function _asPolicy(p) {
    return p && p._normalized === true ? p : policyOf(p);
  }

  // Policy row in effect on a local work date, or null (no rate on file yet
  // for that date — flag it, never fabricate one).
  function resolvePolicy(policy, ymd) {
    const p = _asPolicy(policy);
    if (!ymd) return null;
    let hit = null;
    for (const e of p.history) {
      if (e.effectiveFrom <= ymd) hit = e; // sorted asc; ties already ordered by setAt
      else break;
    }
    return hit;
  }

  function currentPolicy(policy, todayYmd) {
    return resolvePolicy(policy, todayYmd || ymdOf(new Date()));
  }

  // ── approval before payroll (TASK-9439) ───────────────────────────────────
  // settings → { blockExport, history[] sorted asc }. Malformed rows are
  // DROPPED; unknown type keys are dropped from a row (never guessed).
  function approvalPolicyOf(settings) {
    const s = settings || {};
    const pa = s.payrollApproval && typeof s.payrollApproval === 'object' ? s.payrollApproval : {};
    const hist = (Array.isArray(pa.history) ? pa.history : [])
      .filter(e => e && typeof e === 'object' && typeof e.effectiveFrom === 'string'
        && /^\d{4}-\d{2}-\d{2}$/.test(e.effectiveFrom) && Array.isArray(e.types))
      .map(e => ({
        effectiveFrom: e.effectiveFrom,
        types: e.types.filter(t => APPROVAL_TYPES.indexOf(t) !== -1),
        setBy: e.setBy || null,
        setAt: num(e.setAt) || 0,
      }))
      .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : a.effectiveFrom > b.effectiveFrom ? 1 : a.setAt - b.setAt));
    return { blockExport: pa.blockExport === true, history: hist };
  }

  // Does `type` need approval on work date `ymd`? The dated row in effect on
  // that date wins; before the first row (or with none) the 1483 baseline
  // (personalVehicle.requireApproval) decides for 'vehicle'. No ymd → the
  // latest row (today's view).
  function approvalRequiredOn(policy, ymd, type) {
    const p = _asPolicy(policy);
    const ty = type || 'vehicle';
    const hist = (p.approval && p.approval.history) || [];
    let hit = null;
    if (hist.length) {
      if (!ymd) hit = hist[hist.length - 1];
      else for (const e of hist) { if (e.effectiveFrom <= ymd) hit = e; else break; }
    }
    if (hit) return hit.types.indexOf(ty) !== -1;
    return ty === 'vehicle' ? p.requireApproval === true : false;
  }

  // Dated rows ONLY — no 1483 baseline. COD round 1 (Code Reviewer ❌): the
  // hours-only flat day never needed approval under 1483, so gating it on the
  // un-dated requireApproval flag would re-cut every past period of a tenant
  // that already had it on. It needs approval only from the date a tenant
  // ticks the type on the new, effective-dated card.
  function approvalRequiredDatedOn(policy, ymd, type) {
    const p = _asPolicy(policy);
    const hist = (p.approval && p.approval.history) || [];
    let hit = null;
    if (ymd) for (const e of hist) { if (e.effectiveFrom <= ymd) hit = e; else break; }
    return !!hit && hit.types.indexOf(type || 'vehicle') !== -1;
  }

  // The status a review still holds, or null when it is missing or STALE.
  // A decision is bound to the miles it looked at AND (TASK-9439) the amount
  // it would have paid: if either changed afterwards, it is unreviewed again.
  function reviewBound(rv, miles, amount) {
    const st = rv && rv.status;
    if (st !== 'approved' && st !== 'denied') return null;
    if (num(rv.miles) === null || round2(num(rv.miles)) !== round2(num(miles) || 0)) return null;
    const ra = num(rv.amount);
    if (ra !== null && amount !== undefined && amount !== null && round2(ra) !== round2(amount)) return null;
    return st;
  }

  // The amount a review of this entry is bound to: per_mile = miles × rate,
  // flat_per_day = the day's flat. null = no rate on file (nothing to bind).
  function reviewAmountOf(pol, miles) {
    if (!pol) return null;
    return pol.basis === 'flat_per_day' ? round2(pol.flatPerDay) : round2((num(miles) || 0) * pol.ratePerMile);
  }

  // Miles a review of this entry binds to (0 for an entry with no claim —
  // an hours-only day's entries).
  function reviewMilesOf(entry) {
    return hasClaim(entry) ? round2(num(entry.personalVehicleMiles)) : 0;
  }

  // Rows → what stands between this period and payroll: items that NEED
  // approval and have none yet (pending), and items a reviewer denied ($0).
  // A row that does not qualify, has no rate, or is not enrolled pays nothing
  // either way and is reported elsewhere, so it is not an approval item.
  // TASK-9451 — opts.entries (the period's raw clock entries, open ones
  // included): a PENDING flat-per-day claim item whose person-day still has a
  // shift with a claim and no clock-out gets openShift:true. The Claims list
  // groups that open shift into the day's row and will not let anyone approve
  // it until it closes, so Payroll Prep must say why ("clock-out still open")
  // instead of a Review link that leads nowhere. (A per-mile row is one entry,
  // and an hours-only day is approvable from its completed entries: neither
  // is held up by an open shift.)
  function approvalItems(rows, opts) {
    const pending = [], denied = [];
    const openDays = new Set();
    ((opts && opts.entries) || []).forEach(e => {
      if (!e || !e.personId || !e.status || e.status === 'completed' || !hasClaim(e)) return;
      const y = ymdOf(e.clockIn);
      if (y) openDays.add(`${e.personId}|${y}`);
    });
    (rows || []).forEach(r => {
      if (!r || r.status === 'ineligible' || r.noRate || r.qualifies === false || r.open) return;
      const it = { key: r.key, type: r.type || 'vehicle', personId: r.personId, personName: r.personName || '',
        ymd: r.ymd, amount: round2(r.amount || 0), miles: r.miles || 0, hoursOnly: !!r.hoursOnly };
      if (r.dayHeld) it.dayHeld = true;
      if (r.status === 'pending' && r.basis === 'flat_per_day' && !r.hoursOnly && openDays.has(`${r.personId}|${r.ymd}`)) it.openShift = true;
      if (r.status === 'pending') pending.push(it);
      else if (r.status === 'denied') denied.push(it);
    });
    return { pending, denied };
  }

  // ── capture validation (driver + admin forms) ─────────────────────────────
  // Returns { ok, fields, error }. `fields` is EXACTLY what the form writes —
  // the two driver-owned keys and nothing else. Miles is REQUIRED when the
  // toggle is on: an unsubstantiated claim defeats the tax treatment the
  // feature exists for.
  function validateClaim(used, rawMiles) {
    if (!used) return { ok: true, fields: { personalVehicleUsed: false, personalVehicleMiles: null }, error: null };
    const s = rawMiles === null || rawMiles === undefined ? '' : String(rawMiles).trim();
    if (s === '') return { ok: false, fields: null, error: 'Enter the miles you drove your own vehicle (required).' };
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return { ok: false, fields: null, error: 'Miles must be a number, like 42 or 42.5.' };
    const m = parseFloat(s);
    if (!(m > 0)) return { ok: false, fields: null, error: 'Miles must be more than 0 — or turn Personal Vehicle off.' };
    if (m > MAX_MILES) return { ok: false, fields: null, error: `Miles can't be more than ${MAX_MILES} for one shift. Split it across shifts or ask the office.` };
    return { ok: true, fields: { personalVehicleUsed: true, personalVehicleMiles: round2(m) }, error: null };
  }

  function hasClaim(entry) {
    return !!entry && entry.personalVehicleUsed === true && (num(entry.personalVehicleMiles) || 0) > 0;
  }

  // 'approved' | 'denied' | 'pending' (approval on, not reviewed) | 'open'
  // (approval off — visibility only).
  // A review is BOUND to the miles it looked at (COD final-diff Edge Case 🔴):
  // `review.miles` must equal the entry's current miles, or the decision is
  // stale — someone changed the number after it was reviewed — and the claim
  // reads as unreviewed again. A review with no recorded miles binds to
  // nothing and is treated the same way (never paid on a guess).
  // opts: { ymd, amount } — the work date (approval is effective-dated,
  // TASK-9439; default = the entry's clock-in date) and the amount the review
  // must still match (reviewAmountOf; omitted = miles-only binding).
  function claimStatus(entry, policy, opts) {
    const o = opts || {};
    const st = reviewBound(entry && entry.personalVehicleReview, num(entry && entry.personalVehicleMiles) || 0, o.amount);
    if (st) return st;
    const ymd = o.ymd !== undefined ? o.ymd : ymdOf(entry && entry.clockIn);
    return approvalRequiredOn(policy, ymd) ? 'pending' : 'open';
  }

  // Counts toward pay? Denied never pays; where approval is required only
  // approved pays. Second arg: a boolean "approval required", or a policy
  // (+ ymd) to resolve it from.
  function isPayable(status, policyOrRequired, ymd) {
    if (status === 'denied') return false;
    const req = typeof policyOrRequired === 'boolean' ? policyOrRequired
      : (policyOrRequired ? approvalRequiredOn(policyOrRequired, ymd) : false);
    if (req) return status === 'approved';
    return true;
  }

  // ── computation ───────────────────────────────────────────────────────────
  // entries: raw clock entries (any shape with personId, clockIn, the two claim
  // fields). Returns claim ROWS:
  //   per_mile     → one row per entry
  //   flat_per_day → one row per person per local work date (miles summed over
  //                  that day's payable claims; the flat is paid once)
  // Row: { key, personId, personName, ymd, entryIds, miles, basis, ratePerMile,
  //        flatPerDay, amount, nonTaxable, taxableExcess, status, payable,
  //        noRate, adminEntered, customerName, jobTitle, ticketId }
  // Only COMPLETED entries count (an open shift is not a claim yet); pass
  // opts.includeOpen for the review list.
  // opts.isEligible(personId) → true | false | undefined (unknown). COD Code
  // Reviewer (TASK-1483 final diff): the rules fence bounds the claim's SHAPE
  // but cannot cheaply read the person's enrolment, so a raw-SDK claim from a
  // person who is NOT enrolled must be refused HERE, where the money is
  // computed. Such a claim is status 'ineligible' and never pays — unless an
  // admin entered it (their deliberate choice, and rules keep drivers from
  // forging that marker). `false` is required to refuse; `undefined` (person
  // not loaded) does not silently zero someone's pay.
  function computeClaims(entries, policyIn, opts) {
    const o = opts || {};
    const policy = _asPolicy(policyIn);
    const perEntry = [];
    (entries || []).forEach(e => {
      if (!hasClaim(e)) return;
      if (!o.includeOpen && e.status && e.status !== 'completed') return;
      const ymd = ymdOf(e.clockIn);
      if (!ymd) return;
      const adminEntered = !!(e.personalVehicleEnteredBy && e.personalVehicleEnteredBy.source === 'admin');
      const ineligible = !adminEntered && typeof o.isEligible === 'function' && o.isEligible(e.personId) === false;
      const pol = resolvePolicy(policy, ymd);
      const miles = round2(num(e.personalVehicleMiles) || 0);
      const req = approvalRequiredOn(policy, ymd);
      const reviewAmount = reviewAmountOf(pol, miles);
      const status = ineligible ? 'ineligible' : claimStatus(e, policy, { ymd, amount: reviewAmount });
      perEntry.push({
        e, ymd, status, req, reviewAmount,
        open: !!(e.status && e.status !== 'completed'),
        payable: !ineligible && isPayable(status, req),
        miles,
        pol,
      });
    });

    const rows = [];
    const flatGroups = new Map();
    perEntry.forEach(x => {
      const pol = x.pol;
      const base = {
        personId: x.e.personId || null,
        personName: x.e.personName || '',
        ymd: x.ymd,
        status: x.status,
        payable: x.payable,
        needsApproval: x.req,
        reviewAmount: x.reviewAmount,
        noRate: !pol,
        open: x.open,
        adminEntered: !!(x.e.personalVehicleEnteredBy && x.e.personalVehicleEnteredBy.source === 'admin'),
        customerName: x.e.customerName || '',
        jobTitle: x.e.jobTitle || '',
        ticketId: x.e.ticketId || null,
      };
      if (pol && pol.basis === 'flat_per_day') {
        // Group payable and non-payable separately so a denied entry never
        // earns (or shrinks) the day's flat for the payable ones.
        const gk = `${base.personId}|${x.ymd}|${x.payable ? 'p' : x.status}`;
        let g = flatGroups.get(gk);
        if (!g) {
          g = Object.assign({}, base, {
            key: gk, entryIds: [], miles: 0, basis: 'flat_per_day',
            ratePerMile: pol.ratePerMile, flatPerDay: pol.flatPerDay,
            excessTaxable: pol.excessTaxable === true,
            qualify: pol.qualify || _qualifyOf(null),
            _customers: [], _jobs: [],
          });
          flatGroups.set(gk, g);
          rows.push(g);
        }
        g.entryIds.push(x.e.id);
        if (x.open) g.open = true;
        g.miles = round2(g.miles + x.miles);
        if (base.adminEntered) g.adminEntered = true;
        if (base.customerName && g._customers.indexOf(base.customerName) === -1) g._customers.push(base.customerName);
        if (base.jobTitle && g._jobs.indexOf(base.jobTitle) === -1) g._jobs.push(base.jobTitle);
      } else {
        const rate = pol ? pol.ratePerMile : null;
        const amt = rate === null ? 0 : round2(x.miles * rate);
        rows.push(Object.assign({}, base, {
          key: `${base.personId}|${x.e.id}`, entryIds: [x.e.id], miles: x.miles,
          basis: pol ? pol.basis : null, ratePerMile: rate, flatPerDay: null,
          amount: amt, nonTaxable: amt, taxableExcess: 0, uncovered: 0, excessTaxable: false,
        }));
      }
    });
    // TASK-9459 — a flat-per-day approval judges the WHOLE person-day. When one
    // shift on the day is approved but another is unreviewed/stale (a decision
    // voided because its miles or amount moved), the approved shift used to pay
    // the full daily flat on its own while the other sat in a separate pending
    // row. Now the approved shift folds into the pending row: the day is ONE
    // pending item (dayHeld), nothing pays until every shift on it is settled,
    // and approving the row re-stamps all of its shifts. A DENIED shift is a
    // settled decision and still does not hold the day (unchanged).
    [...flatGroups.keys()].forEach(k => {
      if (!k.endsWith('|pending')) return;
      const pk = k.slice(0, -'pending'.length) + 'p';
      const pg = flatGroups.get(pk), hg = flatGroups.get(k);
      if (!pg || !hg) return;
      hg.heldIds = hg.entryIds.slice(); // the unsettled shifts: a review of this row writes ONLY these
      hg.entryIds = pg.entryIds.concat(hg.entryIds);
      hg.miles = round2(hg.miles + pg.miles);
      if (pg.open) hg.open = true;
      if (pg.adminEntered) hg.adminEntered = true;
      pg._customers.forEach(c => { if (hg._customers.indexOf(c) === -1) hg._customers.push(c); });
      pg._jobs.forEach(j => { if (hg._jobs.indexOf(j) === -1) hg._jobs.push(j); });
      hg.dayHeld = true;
      flatGroups.delete(pk);
      rows.splice(rows.indexOf(pg), 1);
    });
    // 'hours' trigger: a day with ≥ minHours worked earns the flat with NO
    // miles claim (how per-diem-style truck pay works). The caller passes the
    // person-days it already aggregated (opts.workDays, from the SAME TASK-939
    // date-hours map payroll uses) — this module never re-derives hours. Only
    // qualifying days become rows, and only for people KNOWN to be enrolled
    // (isEligible === true; unknown never mints pay).
    // TASK-9439 — resolves the 1483 open point: where approval is required on
    // that date, the hours-only day's flat IS vehicle pay the tenant asked to
    // approve, so it needs approval too (it used to pay on hours alone — the
    // exact auto-pay-on-hours shape 1483 was filed to stop). Its decision is
    // written on every completed entry of that person-day (kind 'hoursDay',
    // bound to each entry's miles — 0 without a claim — and the flat), so the
    // SAME review field, rules guard and self-approval block apply. Approval
    // not required: unchanged, it pays like its hours do.
    const dayEntries = new Map();
    (entries || []).forEach(e => {
      if (!e || !e.personId || (e.status && e.status !== 'completed')) return;
      const y = ymdOf(e.clockIn);
      if (!y) return;
      const k = `${e.personId}|${y}`;
      if (!dayEntries.has(k)) dayEntries.set(k, []);
      dayEntries.get(k).push(e);
    });
    // all approved → approved · any denied → denied · else pending. No
    // entries to hold a decision → pending (never paid on a guess).
    const hoursDayStatus = (pid, ymd, flat) => {
      const des = dayEntries.get(`${pid}|${ymd}`) || [];
      if (!des.length) return { status: 'pending', ids: [] };
      const sts = des.map(e => reviewBound(e.personalVehicleReview, reviewMilesOf(e), flat));
      const status = sts.every(s => s === 'approved') ? 'approved' : sts.some(s => s === 'denied') ? 'denied' : 'pending';
      return { status, ids: des.map(e => e.id) };
    };
    const dayHoursOf = (pid, ymd) => {
      if (typeof o.dayHours !== 'function') return undefined;
      const h = num(o.dayHours(pid, ymd));
      return h === null ? undefined : h;
    };
    (o.workDays || []).forEach(wd => {
      if (!wd || !wd.personId || !wd.ymd) return;
      if (typeof o.isEligible !== 'function' || o.isEligible(wd.personId) !== true) return;
      const pol = resolvePolicy(policy, wd.ymd);
      if (!pol || pol.basis !== 'flat_per_day' || !pol.qualify || pol.qualify.mode !== 'hours') return;
      const prefix = `${wd.personId}|${wd.ymd}|`;
      // A PAYABLE claim row for the day already earns (or explains) the flat.
      // A pending/denied claim does NOT erase a day the hours earned (COD
      // day-trigger delta): miles are not the trigger in this mode.
      if (flatGroups.has(`${prefix}p`)) return;
      // TASK-9459 — a held day (approved + unsettled shift) is one pending row already.
      { const _hg = flatGroups.get(`${prefix}pending`); if (_hg && _hg.dayHeld) return; }
      const hasAnyClaim = [...flatGroups.keys()].some(k => k.indexOf(prefix) === 0);
      const req = approvalRequiredDatedOn(policy, wd.ymd);
      // TASK-9439 — approval required and the day's miles claim is still
      // pending: that claim row IS the day's decision (approving it pays the
      // flat the hours earn). A second "hours day" item would ask twice.
      // COD r3 (Edge Case): only a SETTLED pending claim stands in for the day.
      // A group with an open shift can't be approved yet (Claims hides the
      // buttons), while Payroll Prep — which skips open entries — would still
      // list the hours day; suppressing it here deadlocked the export.
      const _pg = flatGroups.get(`${prefix}pending`);
      if (req && _pg && !_pg.open) return;
      const h = dayHoursOf(wd.personId, wd.ymd);
      if (h === undefined) return;
      // Short day with no claim at all: still a row, so Payroll Prep can say
      // why it did not pay ($0, notQualified). A short day that has a claim is
      // already explained by that claim's own row.
      if (h < pol.qualify.minHours - 1e-9 && hasAnyClaim) return;
      const hd = hoursDayStatus(wd.personId, wd.ymd, round2(pol.flatPerDay));
      // COD round 1 (Code Reviewer ❌): an explicit hours-day DENIAL stands even
      // if the type is later unticked — never pay a day a reviewer refused.
      // Only kind:'hoursDay' decisions count here: a denied miles claim still
      // does not erase an hours day when approval is not required (1483).
      const hdDenied = !req && hd.ids.length > 0 && (dayEntries.get(`${wd.personId}|${wd.ymd}`) || [])
        .some(e => e.personalVehicleReview && e.personalVehicleReview.kind === 'hoursDay'
          && reviewBound(e.personalVehicleReview, reviewMilesOf(e), round2(pol.flatPerDay)) === 'denied');
      const g = {
        personId: wd.personId, personName: wd.personName || '', ymd: wd.ymd,
        status: req ? hd.status : (hdDenied ? 'denied' : 'hours'), payable: req ? hd.status === 'approved' : !hdDenied,
        needsApproval: req, reviewAmount: round2(pol.flatPerDay),
        noRate: false, open: false, adminEntered: false,
        customerName: '', jobTitle: '', ticketId: null, hoursOnly: true,
        key: `${prefix}h`, entryIds: hd.ids, miles: 0, basis: 'flat_per_day',
        ratePerMile: pol.ratePerMile, flatPerDay: pol.flatPerDay,
        excessTaxable: pol.excessTaxable === true, qualify: pol.qualify,
        _customers: [], _jobs: [],
      };
      flatGroups.set(g.key, g);
      rows.push(g);
    });
    rows.forEach(r => {
      if (r.basis !== 'flat_per_day') return;
      // Does this day earn the flat under the trigger in effect on its date?
      const q = r.qualify || _qualifyOf(null);
      const h = dayHoursOf(r.personId, r.ymd);
      const needMiles = q.mode !== 'hours', needHours = q.mode !== 'miles';
      const milesOk = r.miles > 0;
      const hoursOk = h !== undefined && h >= q.minHours - 1e-9;
      r.dayHours = h === undefined ? null : round2(h);
      r.qualifies = (!needMiles || milesOk) && (!needHours || hoursOk);
      const hTxt = h === undefined ? 'hours not known' : `${round2(h)}h worked`;
      if (r.qualifies) {
        r.qualifyReason = q.mode === 'miles' ? `qualifies: ${r.miles} mi entered`
          : q.mode === 'hours' ? `qualifies: ${hTxt} (≥ ${q.minHours}h)`
          : `qualifies: ${r.miles} mi and ${hTxt} (≥ ${q.minHours}h)`;
      } else {
        const why = [];
        if (needMiles && !milesOk) why.push('no miles entered');
        if (needHours && !hoursOk) why.push(h === undefined ? 'hours not known' : `${hTxt}, needs ≥ ${q.minHours}h`);
        r.qualifyReason = `not paid: ${why.join(' and ')}`;
        r.amount = 0; r.nonTaxable = 0; r.taxableExcess = 0; r.uncovered = 0;
        r.customerName = r._customers.join(', ');
        r.jobTitle = r._jobs.join(', ');
        delete r._customers; delete r._jobs;
        return;
      }
      const covered = round2(r.miles * r.ratePerMile);
      r.amount = round2(r.flatPerDay);
      r.uncovered = round2(Math.max(0, r.flatPerDay - covered));
      if (r.excessTaxable) {
        r.nonTaxable = round2(Math.min(r.flatPerDay, covered));
        r.taxableExcess = r.uncovered;
      } else {
        r.nonTaxable = r.amount;
        r.taxableExcess = 0;
      }
      r.customerName = r._customers.join(', ');
      r.jobTitle = r._jobs.join(', ');
      delete r._customers; delete r._jobs;
    });
    rows.sort((a, b) => (a.ymd < b.ymd ? -1 : a.ymd > b.ymd ? 1 : String(a.personName).localeCompare(String(b.personName))));
    return rows;
  }

  // rows → { [personId]: { miles, days, amount, nonTaxable, taxableExcess,
  //   pending, denied, noRate } } over PAYABLE rows only (the flags count the
  //   rest so payroll can say what was left out and why).
  function summarize(rows) {
    const out = {};
    (rows || []).forEach(r => {
      const k = r.personId || '_unknown';
      const s = out[k] || (out[k] = { miles: 0, days: 0, amount: 0, nonTaxable: 0, taxableExcess: 0, uncovered: 0, pending: 0, denied: 0, noRate: 0, ineligible: 0, notQualified: 0, _days: {} });
      if (r.status === 'ineligible') { s.ineligible++; return; }
      if (r.noRate) { s.noRate++; return; }
      if (r.qualifies === false) { s.notQualified++; return; }
      if (!r.payable) { if (r.status === 'denied') s.denied++; else s.pending++; return; }
      s.miles = round2(s.miles + r.miles);
      s.amount = round2(s.amount + r.amount);
      s.nonTaxable = round2(s.nonTaxable + r.nonTaxable);
      s.taxableExcess = round2(s.taxableExcess + r.taxableExcess);
      s.uncovered = round2(s.uncovered + (r.uncovered || 0));
      s._days[r.ymd] = true;
    });
    Object.keys(out).forEach(k => { out[k].days = Object.keys(out[k]._days).length; delete out[k]._days; });
    return out;
  }

  // The one label payroll prints next to a vehicle figure. States what DD
  // DID (how the money was routed, per the tenant's own setting) — never a tax
  // conclusion. The tenant and their payroll provider decide the treatment.
  function taxNote(basis, excessTaxable) {
    if (basis === 'flat_per_day') {
      return excessTaxable
        ? 'Flat daily amount: the part covered by miles × rate is exported as a reimbursement; the part the miles don’t cover is exported as wages (Bonus column), per your Claims setting.'
        : 'Flat daily amount: exported in full as a reimbursement, per your Claims setting. The part the miles don’t cover is shown for information.';
    }
    return 'Paid per mile driven (miles × your company rate), exported as a reimbursement.';
  }

  return {
    MAX_MILES, MAX_RATE_PER_MILE, MAX_FLAT_PER_DAY, BASES, SERVER_ONLY_KEYS, QUALIFY_MODES, DEFAULT_MIN_HOURS,
    DEFAULT_PAY_TYPE_NAME, MAX_PAY_TYPE_NAME, cleanPayTypeName, payTypeNameOf,
    round2, ymdOf, isEligible, policyOf, resolvePolicy, currentPolicy,
    validateClaim, hasClaim, claimStatus, isPayable, computeClaims, summarize, taxNote,
    // TASK-9439 — approval before payroll
    APPROVAL_TYPES, approvalPolicyOf, approvalRequiredOn, approvalRequiredDatedOn, reviewBound, reviewAmountOf, reviewMilesOf, approvalItems,
  };
});
